import type { DropdownItemProp, DropdownRef } from 'js/src/components/common/ui/Dropdown';
import type { ImmutableMap } from 'js/src/libs/consts';

export const DEFAULT_SCREEN_DEVICE_ID = 'default';

export type MediaRecorderState = MediaRecorder['state'];

export type WithMediaRecorderGeneratedProps = {
    // <video/> or <audio/>, where the src is the device stream when recording, and plays back the captured blob when recording is finished
    RecordingElement: React.ReactElement<HTMLVideoElement>;
    // the MediaStream, in webcam recording it's a canvas stream (mixed + modified), for voiceover it's the microphone audio stream
    recordingStream: MediaStream | null;
    // calls mediaRecorder.startRecording()
    startRecording: () => void;
    // checks mediaRecorder state to see if it's active, then calls mediaRecorder.pauseRecording()
    pauseRecording: () => void;
    // calls mediaRecorder.resumeRecording
    resumeRecording: () => void;
    // checks mediaRecorder state to see if it's active, then calls mediaRecorder.pauseRecording()
    stopRecording: () => Promise<void>;
    // stops all recording streams, also resets media-recorder's internal clock to 0:00
    resetRecording: () => void;
    // gets VideoInputs and AudioInputs from mediaRecorder, also returns device ids that are active
    enumerateDevices: () => Promise<
        | {
              audioDevices: DeviceInfo[];
              cameraDevices?: DeviceInfo[] | undefined;
              activeAudioDevice?: string;
              activeVideoDevice?: string;
          }
        | undefined
    >;
    // initial setup to get video/audio inputs, then connects the stream to RecordingElement
    // returns a promise because getting inputs is async op
    // takes constraints as a param, can be used to choose a specific device for recording feed
    setupDevices: (constraints: DeviceConstraint) => Promise<void>;
    // clears RecordingElement src and reconnects it with the data blob generated from the recording
    switchToPlayback: (blob: Blob) => void;
    // returns the video blob if webcam recording, audio blob if voiceover recording
    getBlob: () => Blob | null;
    // returns the internal clock of mediaRecorder - useful to keep track of recording duration
    getRecordingTime: () => number;
    // returns state of mediaRecorder
    getRecordingState: () => MEDIA_RECORDER_STATES;
};

export const DEVICES_DROPDOWN_NAME = 'devicesDropdown';
export const CANVAS_CAPTURE_STREAM_FPS = 30;

export type DeviceInfo = {
    label: string;
    id: string;
};

export enum DeviceTypes {
    AUDIO = 'audio',
    VIDEO = 'video',
    SCREEN = 'screen',
}

export type DeviceInfoForDeviceType = {
    [DeviceTypes.AUDIO]: DeviceInfo[];
    [DeviceTypes.VIDEO]: DeviceInfo[];
    [DeviceTypes.SCREEN]: DeviceInfo[];
};

export enum RecordingType {
    AUDIO = 'audio',
    VIDEO = 'video',
}

export enum MimeType {
    CHROME_OPERA_AUDIO_MIME_TYPE = 'audio/webm',
    FIREFOX_AUDIO_MIME_TYPE = 'audio/ogg',
    SAFARI_AUDIO_MIME_TYPE = 'audio/mp4',
    CHROME_VIDEO_MIME_TYPE = 'video/webm',
    FIREFOX_VIDEO_MIME_TYPE = 'video/webm', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
    SAFARI_VIDEO_MIME_TYPE = 'video/mp4',
}

export type DeviceConstraint = {
    audio?: Record<any, any>;
    video?: Record<any, any>;
};

/**
 * MediaRecorder states, should reflect exactly the states as the browser's MediaRecorder.state
 * @deprecated Use MediaRecorderState type instead
 */
export enum MEDIA_RECORDER_STATES {
    INACTIVE = 'inactive',
    RECORDING = 'recording',
    PAUSED = 'paused',
}

export enum RECORD_MODAL_STATE {
    START = 'start',
    UNSUPPORTED = 'unsupported',
    PERMISSION_DENIED = 'denied',
    SETUP = 'setup',
    READY = 'ready',
    COUNTDOWN = 'countdown',
    RECORDING = 'recording',
    PAUSED = 'paused',
    FINISH_RECORDING = 'finish_recording',
    READY_FOR_UPLOAD = 'ready_for_upload',
}

export type DeviceDropdownItemType = Immutable.List<DropdownItemProp<DeviceConstraint>>;

export type ModalContentProps = {
    uiState: RECORD_MODAL_STATE;
    showingPrompter: boolean;
    hidePrompter: () => void;
    handleClockCycleCallback: (timeInMs: number) => void;
    showingRetakeOverlay: boolean;
    handleRetake: () => void;
    hideRetakeOverlay: () => void;
    showDurationWarning: boolean;
    handlePrompterToggleClicked: () => void;
    parsedCount: string;
    showRetakeOverlay: () => void;
    handleStartCountdown: () => void;
    handleResume: () => void;
    handleFinishRecording: () => void;
    handleUploadRecording: () => void;
    handlePause: () => void;
    ModalSettingsToggle: Immutable.Collection<unknown, unknown>;
    deviceInputItems: DeviceDropdownItemType; // the dropdown has a mixture of headers and input info
    handleDeviceSelection: (selectedOption: ImmutableMap<DeviceConstraint>) => void;
    deviceSelectionRef: React.RefObject<DropdownRef>;
} & Pick<
    WithMediaRecorderGeneratedProps,
    'getRecordingTime' | 'getRecordingState' | 'RecordingElement'
>;
